import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h1 {
    font-size: 2.5rem;
    margin-bottom: 20px;
    color: #333;
  }

  p {
    font-size: 1.2rem;
    color: #666;
    max-width: 800px;
    margin: 0 auto;
  }
`;

const AgentTypesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AgentTypeCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-5px);
  }

  h2 {
    font-size: 1.8rem;
    margin-bottom: 20px;
    color: #333;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const StepsList = styled.div`
  margin: 20px 0;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;

  .number {
    background: #007bff;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    flex-shrink: 0;
  }

  .content {
    h3 {
      margin: 0 0 5px;
      font-size: 1.1rem;
      color: #333;
    }

    p {
      margin: 0;
      color: #666;
      font-size: 0.95rem;
      line-height: 1.4;
    }
  }
`;

const ActionButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  margin-top: 20px;

  &:hover {
    background: #0056b3;
  }
`;

const StartPage = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <WelcomeSection>
        <h1>Welcome to ShowingBooker</h1>
        <p>
          Choose your role below to get started with our streamlined showing management system.
          Whether you're listing properties or representing buyers, we've got you covered.
        </p>
      </WelcomeSection>

      <AgentTypesContainer>
        <AgentTypeCard>
          <h2>🏠 Listing Agent</h2>
          <StepsList>
            <Step>
              <div className="number">1</div>
              <div className="content">
                <h3>Add Your Listing</h3>
                <p>Create a new listing with property details, photos, and availability schedule.</p>
              </div>
            </Step>
            <Step>
              <div className="number">2</div>
              <div className="content">
                <h3>Add Owner & Resident Info</h3>
                <p>Enter owner and current resident contact information for showing coordination.</p>
              </div>
            </Step>
            <Step>
              <div className="number">3</div>
              <div className="content">
                <h3>Share Your Link</h3>
                <p>Get a unique booking link to share with buyer's agents for easy showing scheduling.</p>
              </div>
            </Step>
          </StepsList>
          <ActionButton onClick={() => navigate('/listings/add')}>
            Add Your First Listing
          </ActionButton>
        </AgentTypeCard>

        <AgentTypeCard>
          <h2>👥 Buyer's Agent</h2>
          <StepsList>
            <Step>
              <div className="number">1</div>
              <div className="content">
                <h3>Track Your Buyers</h3>
                <p>Manage your buyer clients and their property preferences in one place.</p>
              </div>
            </Step>
            <Step>
              <div className="number">2</div>
              <div className="content">
                <h3>Schedule Showings</h3>
                <p>Book showings for available properties through ShowingBooker links.</p>
              </div>
            </Step>
            <Step>
              <div className="number">3</div>
              <div className="content">
                <h3>Manage Client Pipeline</h3>
                <p>Track showings, feedback, and follow-ups in the CRM system.</p>
              </div>
            </Step>
          </StepsList>
          <ActionButton onClick={() => navigate('/buyers/add')}>
            Add Your First Buyer
          </ActionButton>
        </AgentTypeCard>
      </AgentTypesContainer>
    </PageContainer>
  );
};

export default StartPage;